import React, { useState, useCallback, useRef, useEffect } from "react";
import { PROTEINS } from "../../data/proteins";
import ServingAdjuster from "./ServingAdjuster";

const getUnit = (item) => {
  if (item.toLowerCase().includes("egg")) {
    return "egg(s)";
  } else if (item.toLowerCase().includes("protein")) {
    return "scoop(s)";
  } else if (!PROTEINS.find((p) => p.name === item)) {
    return "serving(s)";
  } else {
    return "oz";
  }
};

const ProteinTracker = ({
  entries,
  onChange,
  isVisible,
  note,
  onNoteChange,
}) => {
  const adjustEntry = useCallback((entries, idx, delta) => {
    const newEntries = [...entries];
    const newOz = Math.max(0.5, newEntries[idx].oz + delta);
    const existing = newEntries[idx];
    const gramsPerOz =
      PROTEINS.find((p) => p.name === existing.item)?.grams ??
      existing.grams / existing.oz;
    newEntries[idx].oz = newOz;
    newEntries[idx].grams = newOz * gramsPerOz;
    return newEntries;
  }, []);

  const [selectedProtein, setSelectedProtein] = useState("");
  const [selectedCustomProtein, setSelectedCustomProtein] = useState("");
  const [gramsPerServing, setGramsPerServing] = useState(1);

  const [oz, setOz] = useState(1);
  const [customServings, setCustomServings] = useState(1);
  const [showCustom, setShowCustom] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);

  const [customProteins, setCustomProteins] = useState(() => {
    const stored = localStorage.getItem("custom.custom_proteins");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Ensure focusing input always shows dropdown if showCustom is false
  useEffect(() => {
    if (
      !showCustom &&
      document.activeElement === dropdownRef.current?.querySelector("input")
    ) {
      setDropdownVisible(true);
    }
  }, [showCustom]);

  const handleAdd = () => {
    const proteinInfo =
      PROTEINS.find((p) => p.name === selectedProtein) ||
      customProteins.find((p) => p.name === selectedProtein);

    if (!proteinInfo) return;

    const grams = proteinInfo.grams * oz;
    const entry = { item: selectedProtein, grams, oz };
    onChange([...entries, entry]);
    setSelectedProtein("");
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="tw-px-4 tw-mb-4">
      {entries.map((entry, idx) => (
        <div
          key={idx}
          className="tw-mb-3 tw-grid tw-grid-cols-[1fr_2fr_auto] tw-gap-2 tw-bg-green-50 dark:tw-bg-gray-900 tw-py-4 tw-px-6 tw-rounded-xl"
        >
          <div className="entry tw-flex tw-flex-col">
            <span className="tw-col-span-1 w-flex tw-text-base tw-items-center">
              {entry.item}
            </span>
            <span className="tw-text-gray-500 tw-text-sm">
              {entry.grams.toFixed(0)}g protein
            </span>
          </div>
          <div className="tw-flex tw-order-3 lg:tw-order-none tw-items-center">
            <ServingAdjuster
              value={entry.oz}
              onChange={(newVal) => {
                const delta = newVal - entry.oz;
                const newEntries = adjustEntry(entries, idx, delta);
                onChange(newEntries);
              }}
              showUnit
              unitLabel={getUnit(entry.item)}
            />
          </div>
          <button
            className="tw-text-gray-500 tw-order-4 lg:tw-order-none hover:tw-text-red-500 lg:tw-col-span-1 tw-min-w-[44px] tw-max-w-[44px] tw-min-h-[44px]"
            onClick={() => {
              const newEntries = entries.filter((_, i) => i !== idx);
              onChange(newEntries);
            }}
          >
            ✕
          </button>
        </div>
      ))}

      <label
        className={`tw-block tw-mb-2  ${entries.length > 0 ? "tw-mt-6" : ""}`}
      >
        Add Protein
      </label>
      <div className="tw-flex tw-gap-3 tw-flex-col lg:tw-flex-row">
        <div className="tw-relative tw-w-full" ref={dropdownRef}>
          <input
            type="text"
            placeholder="Search or select a protein"
            value={selectedProtein}
            onChange={(e) => {
              const val = e.target.value;
              const formatted =
                val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
              setSelectedProtein(formatted);
            }}
            onFocus={() => {
              if (!showCustom) {
                setDropdownVisible(true);
              }
            }}
            className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-3 tw-w-full dark:tw-bg-black"
          />
          {dropdownVisible && (
            <div className="tw-absolute tw-z-10 tw-w-full tw-bg-white dark:tw-bg-black tw-border tw-border-gray-300 tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-80">
              <div className="tw-overflow-auto tw-max-h-80">
                {(selectedProtein
                  ? [...customProteins, ...PROTEINS].filter((p) =>
                      p.name
                        .toLowerCase()
                        .includes(selectedProtein.toLowerCase())
                    )
                  : [...customProteins, ...PROTEINS]
                ).map((option) => (
                  <div
                    key={option.name}
                    className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                    onClick={() => {
                      setSelectedProtein(option.name);
                      setDropdownVisible(false);
                    }}
                  >
                    <span className="tw-flex tw-justify-between tw-items-center">
                      <span>{option.name}</span>
                      {"grams" in option && (
                        <span className="tw-text-gray-400 tw-text-xs">
                          {option.grams}g
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div className="tw-sticky tw-bottom-0 tw-bg-white dark:tw-bg-black tw-border-t tw-border-gray-200 tw-px-4 tw-py-3">
                <button
                  className="tw-text-blue-800 tw-text-base "
                  onClick={() => {
                    const customValue = selectedProtein || "";
                    setShowCustom(true);
                    setDropdownVisible(false);
                    setSelectedCustomProtein(customValue);
                    setTimeout(() => setSelectedProtein(""), 0);
                  }}
                >
                  +{" "}
                  <span className="hover:tw-underline">
                    Add {selectedProtein ? "'" + selectedProtein + "' as " : ""}
                    a custom entry
                  </span>
                </button>
              </div>
            </div>
          )}
          {selectedProtein && (
            <button
              type="button"
              onClick={() => setSelectedProtein("")}
              className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pr-2 hover:tw-text-gray-600"
              aria-label="Clear input"
            >
              ×
            </button>
          )}
        </div>
        <button
          className={`tw-px-5 tw-min-w-[80px] tw-rounded-full tw-min-h-[50px] tw-text-sm  ${
            selectedProtein
              ? "tw-bg-green-500 tw-text-white hover:tw-bg-green-600"
              : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed"
          }`}
          disabled={!selectedProtein}
          onClick={handleAdd}
        >
          Add
        </button>
      </div>

      {showCustom && (
        <div className="tw-flex tw-flex-wrap tw-w-full tw-mt-6 tw-gap-3 tw-items-end tw-mb-6">
          <div className="tw-flex-1 lg:tw-flex-[2]">
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-1">
              Custom name
            </label>
            <input
              type="text"
              placeholder="e.g. Lentil soup"
              className="tw-border tw-rounded-lg tw-p-3 tw-w-full dark:tw-bg-black"
              value={selectedCustomProtein}
              onChange={(e) => {
                const val = e.target.value;
                const formatted =
                  val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
                setSelectedCustomProtein(formatted);
              }}
            />
          </div>
          <div className="tw-flex-1">
            <label className="tw-block tw-text-sm tw-font-medium tw-mb-1 tw-flex-1">
              Protein per 1 serving (g)
            </label>
            <input
              type="number"
              step="0.5"
              className="tw-border tw-rounded-lg tw-p-3 tw-w-full dark:tw-bg-black"
              value={gramsPerServing}
              onChange={(e) => setGramsPerServing(parseFloat(e.target.value))}
            />
          </div>
          <button
            type="button"
            className="tw-text-gray-500 hover:tw-text-red-500 tw-min-w-[44px] tw-min-h-[44px] tw-rounded-lg dark:hover:tw-text-white tw-text-sm"
            onClick={() => {
              setShowCustom(false);
              setSelectedCustomProtein("");
            }}
          >
            ✕
          </button>
          <button
            className={`tw-px-5 tw-min-w-[80px] tw-w-full lg:tw-w-auto tw-rounded-full tw-text-sm tw-min-h-[50px] ${
              selectedCustomProtein && gramsPerServing > 0
                ? "tw-bg-green-500 tw-text-white hover:tw-bg-green-600"
                : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed"
            }`}
            disabled={!selectedCustomProtein || gramsPerServing <= 0}
            onClick={() => {
              const entry = {
                item: selectedCustomProtein,
                grams: gramsPerServing,
                oz: 1,
              };
              onChange([...entries, entry]);

              const newCustom = {
                name: selectedCustomProtein,
                grams: gramsPerServing,
              };
              const updatedCustoms = [...customProteins, newCustom];
              setCustomProteins(updatedCustoms);
              localStorage.setItem(
                "custom.custom_proteins",
                JSON.stringify(updatedCustoms)
              );

              setGramsPerServing(1);
              setSelectedCustomProtein("");
              setShowCustom(false);
            }}
          >
            Add
          </button>
        </div>
      )}

      <div className="tw-flex tw-gap-4 tw-flex-col sm:tw-flex-row tw-mt-6">
        <div className="tw-w-full">
          <label className="tw-block tw-font-semibold tw-mb-2">Notes</label>
          <textarea
            rows={2}
            className="tw-border tw-rounded-lg tw-w-full tw-p-4 dark:tw-bg-black"
            value={note || ""}
            onChange={(e) => onNoteChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProteinTracker;
